<template>
	<div>
		<v-alert
			v-if="sessionClosed && archiveHint"
			type="warning"
			variant="tonal"
			density="compact"
			class="mb-3"
		>
			{{ archiveHint }}
		</v-alert>
		<v-expansion-panels v-if="deliberationsBySpecialite.length > 0">
			<v-expansion-panel
				v-for="specialiteGroup in deliberationsBySpecialite"
				:key="specialiteGroup.specialite.id"
			>
				<v-expansion-panel-title>
					<div class="d-flex justify-space-between align-center w-100 pr-4 flex-wrap gap-2">
						<div class="d-flex flex-wrap align-center gap-2 min-width-0">
							<v-icon size="18">mdi-school</v-icon>
							<strong>{{ specialiteGroup.specialite.intitule }}</strong>
							<template v-if="canViewSummary">
								<span
									v-for="(semestreGroup, idx) in specialiteGroup.bySemestre"
									:key="semestreGroup.semestre.id"
									class="d-inline-flex align-center"
								>
									<v-tooltip location="top" :text="formatSemestreSummaryLine(toSemestreSummary(semestreGroup))">
										<template #activator="{ props: tooltipProps }">
											<v-chip
												v-bind="tooltipProps"
												:color="getSemestreSummaryColor(toSemestreSummary(semestreGroup))"
												size="x-small"
												variant="tonal"
												class="spec-sem-chip"
												@click.stop
											>
												{{ formatSemestreCompact(toSemestreSummary(semestreGroup)) }}
											</v-chip>
										</template>
									</v-tooltip>
									<span
										v-if="idx < specialiteGroup.bySemestre.length - 1"
										class="text-medium-emphasis mx-1"
									>·</span>
								</span>
							</template>
						</div>
						<div class="d-flex align-center gap-2 flex-shrink-0">
							<v-menu v-if="canDownloadResultats(specialiteGroup.specialite.id)" location="bottom">
								<template #activator="{ props: menuProps }">
									<v-btn
										v-bind="menuProps"
										size="small"
										color="primary"
										variant="tonal"
										:loading="isCheckingDisponibilite(specialiteGroup.specialite.id)"
										@click.stop
									>
										<v-icon start size="18">mdi-printer</v-icon>
										Impression de résultats
										<v-icon end size="18">mdi-menu-down</v-icon>
									</v-btn>
								</template>
								<v-list density="compact">
									<v-list-item
										v-for="option in impressionOptions"
										:key="option.variant"
										@click.stop="() => emitDownloadResultats(specialiteGroup.specialite, option.variant)"
									>
										<template #prepend>
											<v-icon size="18">{{ option.icon }}</v-icon>
										</template>
										<v-list-item-title>{{ option.title }}</v-list-item-title>
										<v-list-item-subtitle>{{ option.subtitle }}</v-list-item-subtitle>
									</v-list-item>
								</v-list>
							</v-menu>
							<v-chip size="small" color="primary" variant="tonal">
								{{ specialiteGroup.deliberations.length }} délibération(s)
							</v-chip>
						</div>
					</div>
				</v-expansion-panel-title>
				<v-expansion-panel-text>
					<v-expansion-panels>
						<v-expansion-panel
							v-for="semestreGroup in specialiteGroup.bySemestre"
							:key="semestreGroup.semestre.id"
						>
							<v-expansion-panel-title>
								<div class="d-flex justify-space-between align-center w-100 pr-4">
									<div>
										<v-icon left small>mdi-book-open-page-variant</v-icon>
										<strong>Semestre {{ semestreGroup.semestre.numero }}</strong>
									</div>
									<div class="d-flex gap-2">
										<v-chip
											v-if="semestreGroup.premiere"
											size="small"
											color="primary"
											variant="tonal"
											@click.stop="() => navigateTo(`/admin/deliberation/${semestreGroup.premiere.id}`)"
											style="cursor: pointer"
										>
											Première
										</v-chip>
										<v-chip
											v-if="semestreGroup.seconde"
											size="small"
											color="warning"
											variant="tonal"
											@click.stop="() => navigateTo(`/admin/deliberation/${semestreGroup.seconde.id}`)"
											style="cursor: pointer"
										>
											Seconde
										</v-chip>
									</div>
								</div>
							</v-expansion-panel-title>
							<v-expansion-panel-text>
								<v-list>
									<v-list-item
										v-if="semestreGroup.premiere && canViewPredeliberationLink(semestreGroup.premiere)"
										@click="() => navigateTo(`/admin/deliberation/predeliberation/${semestreGroup.premiere.predeliberation_id}`)"
										style="cursor: pointer"
									>
										<v-list-item-title>
											<v-icon left small color="warning">mdi-clipboard-check-outline</v-icon>
											Prédélibération
										</v-list-item-title>
										<v-list-item-subtitle>
											État: {{ getPredeliberationEtatLabel(semestreGroup.premiere.predeliberation?.etat) }} • 
											Responsable pédagogique: {{ getPredeliberationResponsableName(semestreGroup.premiere.predeliberation) }}
										</v-list-item-subtitle>
										<template #append>
											<v-chip :color="getPredeliberationEtatColor(semestreGroup.premiere.predeliberation?.etat)" size="small" variant="tonal">
												{{ getPredeliberationEtatLabel(semestreGroup.premiere.predeliberation?.etat) }}
											</v-chip>
										</template>
									</v-list-item>
									<v-list-item
										v-if="semestreGroup.premiere"
									>
										<v-list-item-title>
											<div class="d-flex align-center gap-2">
												<v-icon left small>mdi-gavel</v-icon>
												<strong>Délibération Première</strong>
												<v-chip :color="getEtatColor(semestreGroup.premiere.etat)" size="small" variant="tonal" class="ml-2">
													{{ getEtatLabel(semestreGroup.premiere.etat) }}
												</v-chip>
											</div>
										</v-list-item-title>
										<v-list-item-subtitle>
											Responsable: {{ getResponsableName(semestreGroup.premiere) }}
										</v-list-item-subtitle>
										<template #append>
											<v-menu>
												<template #activator="{ props: menuProps }">
													<v-tooltip text="Actions disponibles" location="top">
														<template #activator="{ props: tooltipProps }">
															<v-btn
																class="mydit-btn"
																v-bind="{ ...menuProps, ...tooltipProps }"
																size="small"
																elevation="0"
																@click.stop
															>
																<v-icon color="white">mdi-apps</v-icon>
															</v-btn>
														</template>
													</v-tooltip>
												</template>
												<v-list density="compact">
													<v-tooltip text="Voir les détails de la délibération" location="left">
														<template #activator="{ props: tooltipProps }">
															<v-list-item 
																v-bind="tooltipProps"
																@click.stop="() => $emit('view', semestreGroup.premiere)"
															>
																<template #prepend>
																	<v-icon>mdi-eye</v-icon>
																</template>
																<v-list-item-title>Voir</v-list-item-title>
															</v-list-item>
														</template>
													</v-tooltip>
													<v-tooltip 
														v-if="canViewDeliberationRattrapage(semestreGroup.premiere) && semestreGroup.premiere.etat === 'valide'"
														text="Consulter la liste des étudiants concernés par le rattrapage" 
														location="left"
													>
														<template #activator="{ props: tooltipProps }">
															<v-list-item
																v-bind="tooltipProps"
																@click.stop="() => $emit('viewRattrapage', semestreGroup.premiere)"
															>
																<template #prepend>
																	<v-icon>mdi-alert-circle</v-icon>
																</template>
																<v-list-item-title>Rattrapage</v-list-item-title>
															</v-list-item>
														</template>
													</v-tooltip>
													<v-tooltip 
														v-if="canModify(semestreGroup.premiere)"
														text="Modifier les informations de la délibération" 
														location="left"
													>
														<template #activator="{ props: tooltipProps }">
															<v-list-item
																v-bind="tooltipProps"
																@click.stop="() => $emit('edit', semestreGroup.premiere)"
															>
																<template #prepend>
																	<v-icon>mdi-pencil</v-icon>
																</template>
																<v-list-item-title>Modifier</v-list-item-title>
															</v-list-item>
														</template>
													</v-tooltip>
													<v-tooltip 
														v-if="isAdmin && canMutateSession && semestreGroup.premiere.etat === 'annule'"
														text="Réactiver la délibération annulée" 
														location="left"
													>
														<template #activator="{ props: tooltipProps }">
															<v-list-item
																v-bind="tooltipProps"
																@click.stop="() => $emit('reactivate', semestreGroup.premiere)"
															>
																<template #prepend>
																	<v-icon>mdi-restore</v-icon>
																</template>
																<v-list-item-title>Réactiver</v-list-item-title>
															</v-list-item>
														</template>
													</v-tooltip>
													<v-tooltip 
														v-if="canDownloadDeliberationDocuments(semestreGroup.premiere) && semestreGroup.premiere.etat === 'valide'"
														text="Télécharger les documents de la délibération" 
														location="left"
													>
														<template #activator="{ props: tooltipProps }">
															<v-list-item
																v-bind="tooltipProps"
																@click.stop="() => $emit('downloadDocuments', semestreGroup.premiere)"
															>
																<template #prepend>
																	<v-icon>mdi-download</v-icon>
																</template>
																<v-list-item-title>Télécharger les documents</v-list-item-title>
															</v-list-item>
														</template>
													</v-tooltip>
												</v-list>
											</v-menu>
										</template>
									</v-list-item>
									<v-list-item
										v-if="semestreGroup.seconde"
									>
										<v-list-item-title>
											<div class="d-flex align-center gap-2">
												<v-icon left small>mdi-gavel</v-icon>
												<strong>Délibération Seconde</strong>
												<v-chip :color="getEtatColor(semestreGroup.seconde.etat)" size="small" variant="tonal" class="ml-2">
													{{ getEtatLabel(semestreGroup.seconde.etat) }}
												</v-chip>
											</div>
										</v-list-item-title>
										<v-list-item-subtitle>
											Responsable: {{ getResponsableName(semestreGroup.seconde) }}
										</v-list-item-subtitle>
										<template #append>
											<v-menu>
												<template #activator="{ props: menuProps }">
													<v-tooltip text="Actions disponibles" location="top">
														<template #activator="{ props: tooltipProps }">
															<v-btn
																class="mydit-btn"
																v-bind="{ ...menuProps, ...tooltipProps }"
																size="small"
																elevation="0"
																@click.stop
															>
																<v-icon color="white">mdi-apps</v-icon>
															</v-btn>
														</template>
													</v-tooltip>
												</template>
												<v-list density="compact">
													<v-tooltip text="Voir les détails de la délibération" location="left">
														<template #activator="{ props: tooltipProps }">
															<v-list-item 
																v-bind="tooltipProps"
																@click.stop="() => $emit('view', semestreGroup.seconde)"
															>
																<template #prepend>
																	<v-icon>mdi-eye</v-icon>
																</template>
																<v-list-item-title>Voir</v-list-item-title>
															</v-list-item>
														</template>
													</v-tooltip>
													<v-tooltip 
														v-if="canViewDeliberationRattrapage(semestreGroup.seconde) && semestreGroup.seconde.etat === 'valide'"
														text="Consulter la liste des étudiants concernés par le rattrapage" 
														location="left"
													>
														<template #activator="{ props: tooltipProps }">
															<v-list-item
																v-bind="tooltipProps"
																@click.stop="() => $emit('viewRattrapage', semestreGroup.seconde)"
															>
																<template #prepend>
																	<v-icon>mdi-alert-circle</v-icon>
																</template>
																<v-list-item-title>Rattrapage</v-list-item-title>
															</v-list-item>
														</template>
													</v-tooltip>
													<v-tooltip 
														v-if="canModify(semestreGroup.seconde)"
														text="Modifier les informations de la délibération" 
														location="left"
													>
														<template #activator="{ props: tooltipProps }">
															<v-list-item
																v-bind="tooltipProps"
																@click.stop="() => $emit('edit', semestreGroup.seconde)"
															>
																<template #prepend>
																	<v-icon>mdi-pencil</v-icon>
																</template>
																<v-list-item-title>Modifier</v-list-item-title>
															</v-list-item>
														</template>
													</v-tooltip>
													<v-tooltip 
														v-if="isAdmin && canMutateSession && semestreGroup.seconde.etat === 'annule'"
														text="Réactiver la délibération annulée" 
														location="left"
													>
														<template #activator="{ props: tooltipProps }">
															<v-list-item
																v-bind="tooltipProps"
																@click.stop="() => $emit('reactivate', semestreGroup.seconde)"
															>
																<template #prepend>
																	<v-icon>mdi-restore</v-icon>
																</template>
																<v-list-item-title>Réactiver</v-list-item-title>
															</v-list-item>
														</template>
													</v-tooltip>
													<v-tooltip 
														v-if="canDownloadDeliberationDocuments(semestreGroup.seconde) && semestreGroup.seconde.etat === 'valide'"
														text="Télécharger les documents de la délibération" 
														location="left"
													>
														<template #activator="{ props: tooltipProps }">
															<v-list-item
																v-bind="tooltipProps"
																@click.stop="() => $emit('downloadDocuments', semestreGroup.seconde)"
															>
																<template #prepend>
																	<v-icon>mdi-download</v-icon>
																</template>
																<v-list-item-title>Télécharger les documents</v-list-item-title>
															</v-list-item>
														</template>
													</v-tooltip>
												</v-list>
											</v-menu>
										</template>
									</v-list-item>
								</v-list>
							</v-expansion-panel-text>
						</v-expansion-panel>
					</v-expansion-panels>
				</v-expansion-panel-text>
			</v-expansion-panel>
		</v-expansion-panels>
		<v-alert v-else type="info" variant="tonal">
			Aucune délibération pour cette session
		</v-alert>
	</div>
</template>

<script setup lang="ts">
import { computed, ref, watch, onMounted } from 'vue'
import type { Deliberation } from '@/types/deliberation'
import type { ImpressionResultatsVariant } from '@/types/resultatsClasse'
import { usePermissions } from '@/composables/usePermissions'
import { useClosedSessionGuard } from '@/composables/useClosedSessionGuard'
import { useProfileStore } from '@/stores/profile'
import { useResultatsClasse } from '@/composables/useResultatsClasse'
import {
	canDownloadDeliberationDocuments as canDownloadDeliberationDocumentsFor,
	canViewDeliberationRattrapage as canViewDeliberationRattrapageFor,
	canViewPredeliberationLink as canViewPredeliberationLinkFor,
	canViewDeliberationSessionSummary,
} from '@/utils/deliberationAccessHelpers'
import { canDownloadResultatsClasse } from '@/utils/resultatsClasseHelpers'
import {
	formatSemestreCompact,
	formatSemestreSummaryLine,
	getSemestreSummaryColor,
	type DeliberationSemestreSummary,
} from '@/utils/deliberationSessionSummary'

const props = defineProps<{
	session: any
	deliberations: Deliberation[]
}>()

const emit = defineEmits<{
	view: [deliberation: Deliberation]
	edit: [deliberation: Deliberation]
	reactivate: [deliberation: Deliberation]
	viewRattrapage: [deliberation: Deliberation]
	downloadDocuments: [deliberation: Deliberation]
	downloadResultatsClasse: [payload: { session: any; specialite: any; variant: ImpressionResultatsVariant }]
}>()

const { isAdmin, isSuperAdmin, isPedagogicalManager } = usePermissions()
const {
	isClosed: sessionClosed,
	canMutate: canMutateSession,
	archiveHint,
} = useClosedSessionGuard(() => props.session)
const profileStore = useProfileStore()
const { getDisponibilite } = useResultatsClasse()

const disponibiliteBySpecialite = ref<Record<number, boolean>>({})
const checkingSpecialiteIds = ref<Set<number>>(new Set())

const currentPersonnelId = computed(() => {
	const profile = profileStore.profile
	if (!profile) return undefined
	return (profile as any).personnel?.id || (profile as any).personnel_id || (profile as any).id_personnel as number | undefined
})

const accessFlags = computed(() => ({
	isAdmin: isAdmin.value,
	isSuperAdmin: isSuperAdmin.value,
	isPedagogicalManager: isPedagogicalManager.value,
}))

const canViewSummary = computed(() =>
	canViewDeliberationSessionSummary(
		props.session,
		currentPersonnelId.value,
		accessFlags.value,
		props.deliberations
	)
)

const impressionOptions: Array<{
	variant: ImpressionResultatsVariant
	title: string
	subtitle: string
	icon: string
}> = [
	{
		variant: 'classe',
		title: 'Résultats de classe',
		subtitle: 'PDF — nom et prénom',
		icon: 'mdi-file-pdf-box',
	},
	{
		variant: 'classe-anonyme',
		title: 'Résultats de classe anonymisé',
		subtitle: 'PDF — matricule uniquement',
		icon: 'mdi-incognito',
	},
	{
		variant: 'annuel',
		title: 'Résultats annuels',
		subtitle: 'Excel — nom et prénom',
		icon: 'mdi-file-excel',
	},
	{
		variant: 'annuel-anonyme',
		title: 'Résultats annuels anonymisé',
		subtitle: 'Excel — matricule uniquement',
		icon: 'mdi-file-excel-outline',
	},
]

function canDownloadResultats(specialiteId: number): boolean {
	if (!canDownloadResultatsClasse(props.session, currentPersonnelId.value, accessFlags.value)) {
		return false
	}
	return disponibiliteBySpecialite.value[specialiteId] === true
}

function isCheckingDisponibilite(specialiteId: number): boolean {
	return checkingSpecialiteIds.value.has(specialiteId)
}

function emitDownloadResultats(specialite: any, variant: ImpressionResultatsVariant) {
	emit('downloadResultatsClasse', { session: props.session, specialite, variant })
}

async function refreshDisponibiliteForSpecialites() {
	if (!canDownloadResultatsClasse(props.session, currentPersonnelId.value, accessFlags.value)) {
		disponibiliteBySpecialite.value = {}
		return
	}

	const specialiteIds = deliberationsBySpecialite.value
		.map((g) => g.specialite?.id)
		.filter((id): id is number => typeof id === 'number' && id > 0)

	await Promise.all(
		specialiteIds.map(async (specialiteId) => {
			checkingSpecialiteIds.value.add(specialiteId)
			try {
				const result = await getDisponibilite(props.session.id, specialiteId)
				disponibiliteBySpecialite.value = {
					...disponibiliteBySpecialite.value,
					[specialiteId]: Boolean(
						result.success && (result.data?.impression_disponible ?? result.data?.disponible)
					),
				}
			} finally {
				checkingSpecialiteIds.value.delete(specialiteId)
			}
		})
	)
}

onMounted(() => {
	refreshDisponibiliteForSpecialites()
})

watch(
	() => [props.deliberations, props.session?.id],
	() => {
		refreshDisponibiliteForSpecialites()
	},
	{ deep: true }
)

function toSemestreSummary(semestreGroup: {
	semestre: { id: number; numero?: number }
	premiere: Deliberation | null
	seconde: Deliberation | null
}): DeliberationSemestreSummary {
	return {
		semestreId: semestreGroup.semestre.id,
		semestreNumero: semestreGroup.semestre.numero ?? 0,
		premiere: semestreGroup.premiere,
		seconde: semestreGroup.seconde,
	}
}

function canDownloadDeliberationDocuments(delib: Deliberation): boolean {
	return canDownloadDeliberationDocumentsFor(delib, currentPersonnelId.value, accessFlags.value)
}

function canViewDeliberationRattrapage(delib: Deliberation): boolean {
	return canViewDeliberationRattrapageFor(delib, currentPersonnelId.value, accessFlags.value)
}

function canViewPredeliberationLink(delib: Deliberation): boolean {
	return canViewPredeliberationLinkFor(delib, currentPersonnelId.value, accessFlags.value)
}

function canModify(delib: Deliberation): boolean {
	if (!isAdmin.value) return false
	if (sessionClosed.value && !canMutateSession.value) return false
	return delib.etat !== 'valide' && delib.etat !== 'annule'
}

const deliberationsBySpecialite = computed(() => {
	const grouped: Record<number, { specialite: any; deliberations: Deliberation[] }> = {}
	
	props.deliberations.forEach(delib => {
		const specId = delib.specialite?.id
		if (!specId) return
		
		if (!grouped[specId]) {
			grouped[specId] = {
				specialite: delib.specialite,
				deliberations: []
			}
		}
		grouped[specId].deliberations.push(delib)
	})
	
	return Object.values(grouped).map(group => ({
		...group,
		bySemestre: group.deliberations
			.reduce((acc: any[], delib) => {
				const semId = delib.semestre?.id
				if (!semId) return acc

				let semGroup = acc.find(s => s.semestre.id === semId)
				if (!semGroup) {
					semGroup = {
						semestre: delib.semestre,
						premiere: null,
						seconde: null
					}
					acc.push(semGroup)
				}

				if (delib.type_deliberation === 'premiere') {
					semGroup.premiere = delib
				} else if (delib.type_deliberation === 'seconde') {
					semGroup.seconde = delib
				}

				return acc
			}, [])
			.sort((a, b) => (a.semestre?.numero ?? 0) - (b.semestre?.numero ?? 0))
	}))
})

function getEtatColor(etat: string): string {
	const colors: Record<string, string> = {
		en_cours: 'info',
		phase_1_terminee: 'primary',
		phase_2_terminee: 'warning',
		valide: 'success',
		annule: 'error'
	}
	return colors[etat] || 'grey'
}

function getEtatLabel(etat: string): string {
	const labels: Record<string, string> = {
		en_cours: 'En cours',
		phase_1_terminee: 'Phase 1 terminée',
		phase_2_terminee: 'Phase 2 terminée',
		valide: 'Validée',
		annule: 'Annulée'
	}
	return labels[etat] || etat
}

function getResponsableName(delib: Deliberation): string {
	return delib.responsable?.personne 
		? `${delib.responsable.personne.nom} ${delib.responsable.personne.prenom}`
		: '-'
}

function getPredeliberationEtatColor(etat?: string): string {
	if (!etat) return 'grey'
	const colors: Record<string, string> = {
		en_cours: 'warning',
		valide: 'success',
		annule: 'error'
	}
	return colors[etat] || 'grey'
}

function getPredeliberationEtatLabel(etat?: string): string {
	if (!etat) return '-'
	const labels: Record<string, string> = {
		en_cours: 'En cours',
		valide: 'Validée',
		annule: 'Annulée'
	}
	return labels[etat] || etat
}

function getPredeliberationResponsableName(predeliberation?: any): string {
	return predeliberation?.responsable?.personne 
		? `${predeliberation.responsable.personne.nom} ${predeliberation.responsable.personne.prenom}`
		: '-'
}
</script>

<style scoped>
.spec-sem-chip {
	height: 20px !important;
	font-size: 10px !important;
}
</style>

